import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  Alert,
  Button
} from '@mui/material';
import {
  Info as AboutIcon,
  TrendingUp as LiveOddsIcon,
  EmojiEvents as ExpertPicksIcon,
  Build as ToolsIcon,
  Group as CommunityIcon,
  Favorite as HeartIcon,
  PhoneInTalk as SupportIcon,
  ArrowBack
} from '@mui/icons-material';

const AboutUsScreen = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const features = [
    {
      icon: <LiveOddsIcon color="primary" />,
      title: 'Live Odds',
      description: 'Always current. Always accurate.'
    },
    {
      icon: <ExpertPicksIcon color="primary" />,
      title: 'Expert Picks',
      description: 'Handicappers you can trust.'
    },
    {
      icon: <ToolsIcon color="primary" />,
      title: 'Exclusive Tools',
      description: 'VIP breakdowns and advanced analysis.'
    },
    {
      icon: <CommunityIcon color="primary" />,
      title: 'Community-Driven',
      description: 'Connect with bettors, share strategies, and debate lines in real time.'
    }
  ];

  return (
    <Box sx={{ py: 2 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
        >
          Back
        </Button>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <AboutIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          About Us
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Empowering bettors with tools, insights, and community
        </Typography>
      </Box>

      {/* Mission Statement Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
            Our Mission
          </Typography>
          <Typography variant="body1" paragraph>
            Bettor Advantage is a next-generation platform built to change the way people engage with sports wagering. Our mission is simple: empower bettors with the tools, insights, and community they need to make smarter, faster, and more informed decisions.
          </Typography>
          <Typography variant="body1" paragraph>
            We combine real-time odds direct from sportsbooks with exclusive insights from proven handicappers, giving our members an unmatched edge. Whether you're an experienced bettor sharpening your strategy or a newcomer eager to learn, Bettor Advantage provides the resources, conversation, and expertise to support your journey.
          </Typography>
        </CardContent>
      </Card>

      {/* What We Offer Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
            What We Offer
          </Typography>
          <List dense>
            {features.map((feature, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon>
                    {feature.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={feature.title}
                    secondary={feature.description}
                    primaryTypographyProps={{ fontWeight: 'medium' }}
                  />
                </ListItem>
                {index < features.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Our Philosophy Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HeartIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
              Our Philosophy
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            At Bettor Advantage, we believe betting is more than numbers—it's about knowledge, timing, and connection. We've built a platform where you're not betting alone, but as part of a community that shares your passion.
          </Typography>
        </CardContent>
      </Card>

      {/* Responsible Gaming Card */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SupportIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
              Our Commitment
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            While we aim to enhance your wagering experience, we are equally committed to responsible gaming. Betting should always remain fun and entertaining. If it ever becomes a concern, we encourage you to seek support through the National Council on Problem Gambling at{' '}
            <Link 
              href="tel:1-800-522-4700" 
              color="primary"
              sx={{ textDecoration: 'none', fontWeight: 'medium' }}
            >
              1-800-522-4700
            </Link>
            {' '}or{' '}
            <Link 
              href="https://www.ncpgambling.org" 
              target="_blank" 
              rel="noopener noreferrer" 
              color="primary"
              sx={{ textDecoration: 'none', fontWeight: 'medium' }}
            >
              https://www.ncpgambling.org
            </Link>
            .
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Remember:</strong> Bet responsibly. Only wager what you can afford to lose. If gambling is no longer fun, it's time to seek help.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AboutUsScreen;
